import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { useResendInvoiceNotice } from './useResendInvoiceNotice';
import type { FamilyBillingInvoice } from './useFamilyBillingQuery';

interface Props {
  invoice: Pick<FamilyBillingInvoice, 'invoiceId' | 'noticeStatus' | 'lastNoticeAt' | 'noticeRecipients' | 'canResendNotice' | 'noticeOutcomeUnknown'>;
  onCompleted: () => void;
}

export function InvoiceNotice({ invoice, onCompleted }: Props) {
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [resend, { loading }] = useResendInvoiceNotice();
  const recipients = invoice.noticeRecipients ?? [];
  const lastNoticeAt = typeof invoice.lastNoticeAt === 'string' ? invoice.lastNoticeAt : null;
  const submit = async () => {
    try {
      const result = await resend({ variables: { input: {
        invoiceId: invoice.invoiceId, confirmUnknown: confirmed,
      } } });
      const payload = result.data?.resendInvoiceNotice;
      const errors = payload?.userErrors?.map((error) => error.message).join(' ');
      if (!payload?.success) {
        setMessage(errors || 'Could not queue the notice.');
        return;
      }
      setMessage(errors || 'Notice queued.');
      setOpen(false);
      onCompleted();
    } catch {
      setMessage('Could not queue the notice. Refresh its status before trying again.');
    }
  };
  return (
    <div className="space-y-1 text-xs">
      <div>{invoice.noticeStatus ?? 'No notice recorded'}</div>
      {lastNoticeAt && <div className="text-muted-foreground">{new Date(lastNoticeAt).toLocaleString()}</div>}
      {recipients.length > 0 && (
        <details>
          <summary className="cursor-pointer">Recipients ({recipients.length})</summary>
          {recipients.map((recipient, index) => (
            <div key={index} className="py-1">
              {recipient.name}: {recipient.email || 'No email'} · {recipient.status}
              {recipient.reason && <div>{recipient.reason}</div>}
            </div>
          ))}
          <div className="text-muted-foreground">Sent means accepted by the email provider.</div>
        </details>
      )}
      {invoice.canResendNotice && (
        <Button size="sm" variant="ghost" onClick={() => { setConfirmed(false); setMessage(null); setOpen(true); }}>
          Resend notice
        </Button>
      )}
      {message && <div role="status">{message}</div>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend invoice notice?</DialogTitle>
            <DialogDescription>This sends a new notice to the family's current guardian email addresses. Prior notices remain in the history.</DialogDescription>
          </DialogHeader>
          {invoice.noticeOutcomeUnknown && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} />
              I checked email-provider activity for the uncertain send and approve sending again.
            </label>
          )}
          {message && <div role="alert">{message}</div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={loading || (invoice.noticeOutcomeUnknown && !confirmed)} onClick={submit}>Queue notice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
